import { cloneDeep } from 'lodash';
import { api } from '../../../services/api';
import { titleCase } from '../../base/common/utils';

class AccountService {
  constructor() {
    this.Api = api;

    // To check if the domain has valid formatting (allow `*.domain.com` style input)
    this.domainRegex = /^(\*(\.)?)?(([a-zA-Z0-9]+[_-])*[a-zA-Z0-9]+)\.[a-zA-Z0-9-_.-]+$/;
  }

  filterParams(params, endpointType) {
    const genericParams = ['full_name', 'short_name', 'url'];
    const sharedEditParams = [
      ...genericParams,
      'affiliate_config',
      'is_pilot',
      'logo_url',
      'notes',
      'show_in_dashboard',
      'widget_options',
      'slug',
      // TODO: uncomment once backend is updated
      // 'categories',
    ];
    const endpointParams = {
      '/publisher/id': [
        ...sharedEditParams,
        'amazon_tracking_id',
        'cogs',
        'has_jstag',
        'reserve_cpc',
        'publisher_type',
        'terms_accepted',
        'user_terms_accepted',
        'datetime_terms_accepted',
      ],
      '/org': [...genericParams, 'publisher_type'],
      '/merchant/id': [
        ...sharedEditParams,
        'ftp_account_name',
        'ftp_account_password',
        'expected_import_frequency',
        'impression_tracker',
        'click_tracker',
        'utm_params',
        'share_goal',
        'is_pacing_enabled',
        'pricing_model',
        'max_cpc',
        'cpc_goal',
        'roi_goal',
        'commission_rate',
        'is_active',
        'is_feed_importer_enabled',
      ],
      '/billing/customers': [
        'merch_id',
        'email',
        'invoicing_enabled',
        'campaign_invoicing_enabled',
      ],
    };

    return Object.keys(params).reduce(
      (acc, curr) =>
        endpointParams[endpointType].includes(curr) ? { ...acc, [curr]: params[curr] } : acc,
      {},
    );
  }

  getAccount(accountType, accountId) {
    return api.get(`/api/v0/${accountType}s/${accountId}/`);
  }

  getAccountUsers(accountType, accountId) {
    return api.get(`/api/v0/admins/${accountType}_info/${accountId}`);
  }

  getPublisherAccounts() {
    const endpoint = '/api/v0/publishers/';
    return this.Api.get(endpoint);
  }

  getMerchantAccounts() {
    const endpoint = '/api/v0/merchants/';
    return this.Api.get(endpoint);
  }

  createAccount(params, accountType) {
    const endpoint = `/api/v0/${accountType}s/`;
    const filteredParams = this.filterParams(params, '/org');
    const [errorMsg, validatedParams, fieldErrors] = this.validateParams(filteredParams);
    if (errorMsg) {
      return Promise.reject([new Error(errorMsg), fieldErrors]);
    }
    return this.Api.post(endpoint, validatedParams);
  }

  saveAccountSlug(params, accountType, accountId) {
    const endpoint = `/api/v0/${accountType}s/${accountId}/`;
    const filteredParams = this.filterParams(params, `/${accountType}/id`);
    const [errorMsg, validatedParams, fieldErrors] = this.validateParams(
      filteredParams,
      accountType,
    );
    if (errorMsg) {
      return Promise.reject([new Error(errorMsg), fieldErrors]);
    }
    return this.Api.put(endpoint, validatedParams);
  }

  editAccount(params, accountType, accountId, affiliateConfigArgs) {
    const endpoint = `/api/v0/${accountType}s/${accountId}/`;
    const filteredParams = this.filterParams(params, `/${accountType}/id`);
    const [errorMsg, validatedParams, fieldErrors] = this.validateParams(
      filteredParams,
      accountType,
    );
    const [linkMateErrors, validatedAffiliateConfig] = this.validateAffiliateConfig(
      affiliateConfigArgs,
      fieldErrors,
    );
    const raiseError = errorMsg || linkMateErrors;
    if (raiseError) {
      return Promise.reject([new Error(raiseError), fieldErrors]);
    }
    const paramsWithLinkConfig = {
      ...validatedParams,
      affiliate_config: { ...validatedParams.affiliate_config, ...validatedAffiliateConfig },
    };
    return this.Api.put(endpoint, paramsWithLinkConfig);
  }

  createUser(params) {
    const [errorMsg, validatedParams, fieldErrors] = this.validateParams(params);
    if (errorMsg) {
      return Promise.reject([new Error(errorMsg), fieldErrors]);
    }
    return api.post('/api/v0/users/', validatedParams);
  }

  editUser(params, id) {
    const [errorMsg, validatedParams, fieldErrors] = this.validateParams(params);
    if (errorMsg) {
      return Promise.reject([new Error(errorMsg), fieldErrors]);
    }
    return api.put(`/api/v0/users/${id}/`, validatedParams);
  }

  getCategories() {
    return {
      data: {
        data: [
          [
            'antelopes',
            'bats',
            'cats',
            'dogs',
            'fashion',
            'consumer electronics',
            'fashion',
            'consumer electronics',
            'fashion',
            'consumer electronics',
            'fashion',
            'consumer electronics',
            'fashion',
            'consumer electronics',
            'fashion',
            'consumer electronics',
            'fashion',
            'consumer electronics',
            'fashion',
            'consumer electronics',
          ],
        ],
      },
    };
  }

  deleteCategory() {}

  editCategories() {}

  saveMerchantBilling(params, stripeId) {
    const filteredParams = this.filterParams(params, '/billing/customers');
    const [errorMsg, validatedParams, fieldErrors] = this.validateParams(filteredParams);
    const validEmail = /^.+@.+\..+$/.test(params.email);
    if (validEmail) {
      if (stripeId) {
        return api.put(`/api/v0/billing/customers/${stripeId}/`, validatedParams);
      }
      return api.post('/api/v0/billing/customers/', validatedParams);
    }
    return Promise.reject([
      new Error('Please use a valid Invoice Recipient email address'),
      ['invoice_recipient'],
    ]);
  }

  validateParams(params, accountType) {
    const newParams = cloneDeep(params);
    const fieldErrors = [];
    let firstErrorMsg;
    const validators = [
      this.checkNonEmpty,
      this.checkLength,
      ...(accountType === 'publisher' ? [this.checkDomains] : []),
    ];

    validators.forEach(validate => {
      if (!firstErrorMsg) {
        Object.entries(newParams).forEach(([param, value]) => {
          let errorMsg;
          if (validate === this.checkDomains) {
            if (param === 'widget_options') {
              errorMsg = validate(this, value.domains);
            }
          } else {
            errorMsg = validate(param, value);
          }

          // Only highlight one field if there's a length error
          if (errorMsg && (!firstErrorMsg || validate !== this.checkLength)) {
            fieldErrors.push(param);
            firstErrorMsg = errorMsg;
          }
        });
      }
    });

    if (newParams.widget_options && newParams.widget_options.domains) {
      newParams.widget_options.domains = this.formatDomains(newParams.widget_options.domains);
    }

    return [firstErrorMsg, newParams, fieldErrors];
  }

  validateAffiliateConfig(affiliateConfigArgs, fieldErrors) {
    const { defaultAffiliate, linkmateAffiliate } = affiliateConfigArgs;
    let configErr;

    if (
      affiliateConfigArgs.hasOwnProperty(defaultAffiliate) &&
      !affiliateConfigArgs[defaultAffiliate]
    ) {
      configErr = `Missing information for ${defaultAffiliate}`;
      fieldErrors.push(defaultAffiliate.replace(/ /g, '-').toLowerCase());
    } else if (
      affiliateConfigArgs.hasOwnProperty(linkmateAffiliate) &&
      !affiliateConfigArgs[linkmateAffiliate]
    ) {
      configErr = `Missing information for ${linkmateAffiliate}`;
      fieldErrors.push(linkmateAffiliate.replace(/ /g, '-').toLowerCase());
    }

    const defAffPayload = this.getDefaultAndLinkmateAffiliateParams(
      defaultAffiliate,
      affiliateConfigArgs,
    );
    const linkAffPayload = this.getDefaultAndLinkmateAffiliateParams(
      linkmateAffiliate,
      affiliateConfigArgs,
    );

    return [configErr, { ...defAffPayload, ...linkAffPayload }];
  }

  getDefaultAndLinkmateAffiliateParams(key, affiliateConfigArgs) {
    const value = affiliateConfigArgs[key];
    const paramMap = {
      Amazon: { amazon_config: { id: value } },
      'Impact Radius': { impact_radius_config: { tracking_url: value } },
      Linkshare: { linkshare_config: { member_id: value } },
      PMG: { pmg_config: { tracking_url: value } },
      Skimlinks: { skimlinks_config: { id: value } },
      Viglink: { viglink_config: { key: value } },
    };
    return paramMap[key] || {};
  }

  removeProtocol(domain) {
    // To strip protocol and trailing slash if any
    return domain.replace(/^https?:\/\/?/, '').replace(/\/$/, '');
  }

  formatDomains(domains) {
    // Remove all empty domains
    let newDomains = cloneDeep(domains);
    newDomains = newDomains.filter(domain => domain);

    newDomains.forEach(domain => {
      const inputDomain = this.removeProtocol(domain);
      if (this.domainRegex.exec(inputDomain)) {
        domain = inputDomain;
      }
    });

    // Remove all duplicate domains
    newDomains = newDomains.filter((domain, index, domains) => domains.indexOf(domain) === index);

    return newDomains;
  }

  checkDomains(self, domains) {
    let errorMsg;
    domains.forEach(domain => {
      const inputDomain = self.removeProtocol(domain);
      if (!self.domainRegex.exec(inputDomain)) {
        errorMsg = `Invalid domain name '${inputDomain}'`;
      }
    });

    return errorMsg;
  }

  checkNonEmpty(param, value) {
    let errorMsg;

    const requiredParams = [
      'full_name',
      'short_name',
      'url',
      'cogs',
      'email',
      'first_name',
      'last_name',
      'invoice_recipient',
    ];

    if (requiredParams.includes(param) && !value && typeof value === 'string') {
      errorMsg = 'Please complete all required fields.';
    }

    return errorMsg;
  }

  checkLength(param, value) {
    let errorMsg;

    const maxLengths = {
      short_name: 49,
      first_name: 100,
      last_name: 100,
      email: 255,
    };

    if (Object.keys(maxLengths).includes(param) && value.length > maxLengths[param]) {
      const formattedParam = param.replace(/_/g, ' ');
      errorMsg = `${titleCase(formattedParam)} must be less than ${maxLengths[param]} characters.`;
    }

    return errorMsg;
  }

  sortByName(orgs) {
    const newOrgs = cloneDeep(orgs);

    return newOrgs.sort((a, b) =>
      a.short_name.toLowerCase() > b.short_name.toLowerCase() ? 1 : -1,
    );
  }
}

export default new AccountService();
